import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Bot,
  Shield,
  ShieldCheck,
  RotateCcw,
  Save,
  RefreshCw,
  Code,
  Sliders,
  Settings,
  AlertCircle,
  CheckCircle2,
  FileCode,
  Brain,
  Bell,
  Cpu,
  Clock,
} from "lucide-react";
import {
  agentsGetConfiguration,
  agentsUpdateConfiguration,
  agentsResetConfiguration,
  agentsListModels,
  agentsListProviders,
} from "../gen";
import type {
  AgentConfigurationResponse,
  ModelCatalogResponse,
  ProviderCatalogItem,
} from "../gen/models";
import { useToast } from "../context/ToastContext";
import { ConfirmModal } from "../components/ConfirmModal";
import { TimeAgo } from "../components/common/TimeAgo";
import { ModelSelector } from "../components/common/ModelSelector";
import {
  ProviderSelector,
  type ProviderPreferencesState,
} from "../components/common/ProviderSelector";
import { useUserDirectory } from "../hooks/useUserDirectory";
import { classifyModelReasoning, getModelIntelligence } from "../utils/modelInfo";

interface AgentConfigDetailProps {
  agentType?: string;
  onConfigUpdated?: () => void;
}

const AGENT_METADATA: Record<
  string,
  { name: string; role: string; description: string; defaultModel: string }
> = {
  frontdesk: {
    name: "Sofia",
    role: "Front Desk & Concierge Operations",
    description:
      "Handles HubSpot live visitor chat inquiries, Silvestre residence amenities, check-in logistics, and hospitality bookings.",
    defaultModel: "gemini-3.8-flash",
  },
  el_protocolo: {
    name: "Sofia",
    role: "Front Desk & Concierge Operations",
    description:
      "Handles HubSpot live visitor chat inquiries, Silvestre residence amenities, check-in logistics, and hospitality bookings.",
    defaultModel: "gemini-3.8-flash",
  },
  protocolo: {
    name: "Sofia",
    role: "Front Desk & Concierge Operations",
    description:
      "Handles HubSpot live visitor chat inquiries, Silvestre residence amenities, check-in logistics, and hospitality bookings.",
    defaultModel: "gemini-3.8-flash",
  },
  sales: {
    name: "Don Silvestre",
    role: "Quotes & Sales Proposals",
    description:
      "Generates seasonal stay quotes, villa packages, and luxury proposals for direct leads and Instagram prospects.",
    defaultModel: "gemini-3.8-flash",
  },
  biblioteca: {
    name: "La Biblioteca",
    role: "Conversational Analytics & Metrics",
    description:
      "Executes read-only SQL queries on Superset PostgreSQL warehouse to summarize marketing leads, revenue, and bookings.",
    defaultModel: "gemini-3.8-flash",
  },
  feedback: {
    name: "Feedback Curation Agent",
    role: "Batch Review & Quality Assurance",
    description:
      "Analyzes conversation threads, drafts rule proposals, and tracks alignment evaluations across agents.",
    defaultModel: "gemini-3.1-flash-lite",
  },
  email_drafting: {
    name: "Email Drafter",
    role: "Gmail Inbound Concierge",
    description:
      "Personalized inbound email drafts, tone calibration, guest CRM context, and automated draft injection.",
    defaultModel: "gemini-3.8-flash",
  },
  template: {
    name: "Template Agent",
    role: "Workflow Prototype",
    description:
      "Multi-turn reference agent demonstrating persistent telemetry, operational rules, and scoped knowledge lookup.",
    defaultModel: "gemini-3.8-flash",
  },
};

export function AgentConfigDetail({
  agentType: propAgentType,
  onConfigUpdated,
}: AgentConfigDetailProps) {
  const { agent_type: routeAgentType } = useParams<{ agent_type: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const agentType = propAgentType || routeAgentType || "frontdesk";
  const { toast } = useToast();
  const { getAgentDisplayName, normalizeAgentType, sanitizeProtocoloName } = useUserDirectory();
  const normalizedType = normalizeAgentType(agentType);

  const [configData, setConfigData] = useState<AgentConfigurationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Active Tab
  const [activeTab, setActiveTab] = useState<
    "guardrails" | "runtime" | "notifications" | "json"
  >("guardrails");

  // Form State
  const [guardrailsEnabled, setGuardrailsEnabled] = useState(true);
  const [onBlock, setOnBlock] = useState<string>("silent");
  const [cannedResponseText, setCannedResponseText] = useState("");
  const [hardBlockThreshold, setHardBlockThreshold] = useState(0.86);
  const [softFlagThreshold, setSoftFlagThreshold] = useState(0.72);
  const [topologicalMargin, setTopologicalMargin] = useState(-0.15);

  const [model, setModel] = useState("gemini-3.8-flash");
  const [maxTokens, setMaxTokens] = useState(4096);
  const [maxTurns, setMaxTurns] = useState(25);
  const [timeoutSeconds, setTimeoutSeconds] = useState(180);
  const [thinkingBudget, setThinkingBudget] = useState<number | null>(null);
  const [modelCatalog, setModelCatalog] = useState<ModelCatalogResponse | null>(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [availableProviders, setAvailableProviders] = useState<ProviderCatalogItem[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providerPreferences, setProviderPreferences] = useState<ProviderPreferencesState>({});

  // Review Notifications state
  const [reviewNotificationsEnabled, setReviewNotificationsEnabled] = useState(false);
  const [reviewerMentions, setReviewerMentions] = useState("");
  const [nudgeScheduleSeconds, setNudgeScheduleSeconds] = useState("300, 600, 900");
  const [maxNudges, setMaxNudges] = useState(3);

  // Raw JSON state
  const [rawJsonText, setRawJsonText] = useState("");
  const [rawJsonError, setRawJsonError] = useState<string | null>(null);

  useEffect(() => {
    async function loadModels() {
      setLoadingModels(true);
      try {
        const resp = await agentsListModels();
        if ("data" in resp && resp.status === 200) {
          setModelCatalog(resp.data);
        }
      } catch (err) {
        console.warn("Could not load dynamic models catalog:", err);
      } finally {
        setLoadingModels(false);
      }
    }
    loadModels();
  }, []);

  useEffect(() => {
    async function loadProviders() {
      if (!model) return;
      setLoadingProviders(true);
      try {
        const resp = await agentsListProviders({ model_id: model });
        if ("data" in resp && resp.status === 200) {
          setAvailableProviders(resp.data.providers || []);
        }
      } catch (err) {
        console.warn("Could not load OpenRouter providers catalog for model:", err);
      } finally {
        setLoadingProviders(false);
      }
    }
    loadProviders();
  }, [model]);

  const meta = AGENT_METADATA[normalizedType] || AGENT_METADATA[agentType] || {
    name: sanitizeProtocoloName(agentType.toUpperCase(), "SOFIA"),
    role: "Autonomous Agent",
    description: "Configurable agent runtime environment and safety barrier.",
    defaultModel: "gemini-3.8-flash",
  };
  const displayName = getAgentDisplayName(normalizedType, meta.name);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await agentsGetConfiguration(agentType);
      const data = (res as any).data as AgentConfigurationResponse;
      setConfigData(data);

      const conf = (data.configuration || {}) as Record<string, any>;
      const gr = (conf.guardrails || {}) as Record<string, any>;
      setGuardrailsEnabled(gr.enabled !== false);
      setOnBlock(gr.on_block || "silent");
      setCannedResponseText(gr.canned_response_text || "");
      setHardBlockThreshold(
        typeof gr.hard_block_threshold === "number" ? gr.hard_block_threshold : 0.86
      );
      setSoftFlagThreshold(
        typeof gr.soft_flag_threshold === "number" ? gr.soft_flag_threshold : 0.72
      );
      setTopologicalMargin(
        typeof gr.topological_margin_threshold === "number"
          ? gr.topological_margin_threshold
          : -0.15
      );

      const mp = (conf.model_parameters || {}) as Record<string, any>;
      setModel(mp.model || meta.defaultModel);
      setMaxTokens(
        typeof mp.max_tokens === "number"
          ? mp.max_tokens
          : typeof mp.max_output_tokens === "number"
          ? mp.max_output_tokens
          : 4096
      );
      setMaxTurns(
        typeof mp.max_turns === "number"
          ? mp.max_turns
          : 25
      );
      setTimeoutSeconds(
        typeof mp.timeout_seconds === "number"
          ? mp.timeout_seconds
          : typeof conf.timeout_seconds === "number"
          ? conf.timeout_seconds
          : 180
      );
      setThinkingBudget(
        typeof mp.thinking_budget === "number" ? mp.thinking_budget : null
      );

      // Provider preferences (order, fallbacks, latency, throughput, etc.)
      const rawProv = (mp.provider || mp.providers || {}) as Record<string, any>;
      setProviderPreferences({
        order: Array.isArray(rawProv.order)
          ? rawProv.order
          : Array.isArray(rawProv)
          ? rawProv
          : undefined,
        allow_fallbacks:
          typeof rawProv.allow_fallbacks === "boolean"
            ? rawProv.allow_fallbacks
            : typeof rawProv.allowFallbacks === "boolean"
            ? rawProv.allowFallbacks
            : true,
        require_parameters:
          typeof rawProv.require_parameters === "boolean"
            ? rawProv.require_parameters
            : typeof rawProv.requireParameters === "boolean"
            ? rawProv.requireParameters
            : undefined,
        data_collection:
          rawProv.data_collection === "deny" || rawProv.dataCollection === "deny"
            ? "deny"
            : undefined,
        zdr: typeof rawProv.zdr === "boolean" ? rawProv.zdr : undefined,
        enforce_distillable_text:
          typeof rawProv.enforce_distillable_text === "boolean"
            ? rawProv.enforce_distillable_text
            : undefined,
        only: Array.isArray(rawProv.only) ? rawProv.only : undefined,
        ignore: Array.isArray(rawProv.ignore) ? rawProv.ignore : undefined,
        quantizations: Array.isArray(rawProv.quantizations)
          ? rawProv.quantizations
          : undefined,
        sort: typeof rawProv.sort === "string" ? rawProv.sort : undefined,
        preferred_min_throughput:
          typeof rawProv.preferred_min_throughput === "number"
            ? rawProv.preferred_min_throughput
            : typeof rawProv.preferred_min_throughput?.p50 === "number"
            ? rawProv.preferred_min_throughput.p50
            : null,
        preferred_max_latency:
          typeof rawProv.preferred_max_latency === "number"
            ? rawProv.preferred_max_latency
            : typeof rawProv.preferred_max_latency?.p50 === "number"
            ? rawProv.preferred_max_latency.p50
            : null,
        max_price:
          typeof rawProv.max_price === "object" && rawProv.max_price !== null
            ? rawProv.max_price
            : undefined,
      });

      const rn = (conf.review_notifications || {}) as Record<string, any>;
      setReviewNotificationsEnabled(rn.enabled === true);
      setReviewerMentions(rn.mentions || "");
      if (Array.isArray(rn.nudge_schedule_seconds)) {
        setNudgeScheduleSeconds(rn.nudge_schedule_seconds.join(", "));
      } else if (typeof rn.nudge_schedule_seconds === "string") {
        setNudgeScheduleSeconds(rn.nudge_schedule_seconds);
      } else {
        setNudgeScheduleSeconds("300, 600, 900");
      }
      setMaxNudges(typeof rn.max_nudges === "number" ? rn.max_nudges : 3);

      setRawJsonText(JSON.stringify(conf, null, 2));
      setRawJsonError(null);
    } catch (err: any) {
      setError(err?.message || "Failed to load agent configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, [agentType]);

  const assembleConfiguration = (): Record<string, any> => {
    if (activeTab === "json") {
      try {
        return JSON.parse(rawJsonText);
      } catch (e: any) {
        throw new Error("Invalid JSON: " + e.message);
      }
    }

    const currentBase = configData?.configuration ? { ...configData.configuration } : {};
    const parsedSchedule = nudgeScheduleSeconds
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0);

    // Assemble clean provider configuration
    const cleanProvider: Record<string, any> = {};
    if (providerPreferences.order && providerPreferences.order.length > 0) {
      cleanProvider.order = providerPreferences.order;
    }
    if (providerPreferences.allow_fallbacks === false) {
      cleanProvider.allow_fallbacks = false;
    }
    if (providerPreferences.require_parameters === true) {
      cleanProvider.require_parameters = true;
    }
    if (providerPreferences.data_collection) {
      cleanProvider.data_collection = providerPreferences.data_collection;
    }
    if (providerPreferences.zdr === true) {
      cleanProvider.zdr = true;
    }
    if (providerPreferences.enforce_distillable_text === true) {
      cleanProvider.enforce_distillable_text = true;
    }
    if (providerPreferences.sort) {
      cleanProvider.sort = providerPreferences.sort;
    }
    if (
      typeof providerPreferences.preferred_min_throughput === "number" &&
      !isNaN(providerPreferences.preferred_min_throughput)
    ) {
      cleanProvider.preferred_min_throughput = providerPreferences.preferred_min_throughput;
    }
    if (
      typeof providerPreferences.preferred_max_latency === "number" &&
      !isNaN(providerPreferences.preferred_max_latency)
    ) {
      cleanProvider.preferred_max_latency = providerPreferences.preferred_max_latency;
    }
    if (providerPreferences.max_price) {
      cleanProvider.max_price = providerPreferences.max_price;
    }

    return {
      ...currentBase,
      guardrails: {
        enabled: guardrailsEnabled,
        on_block: onBlock,
        canned_response_text: cannedResponseText.trim(),
        hard_block_threshold: hardBlockThreshold,
        soft_flag_threshold: softFlagThreshold,
        topological_margin_threshold: topologicalMargin,
      },
      model_parameters: {
        ...(currentBase.model_parameters || {}),
        model,
        max_tokens: maxTokens,
        max_turns: maxTurns,
        timeout_seconds: timeoutSeconds,
        thinking_budget:
          typeof thinkingBudget === "number" && thinkingBudget >= 0
            ? thinkingBudget
            : null,
        provider: Object.keys(cleanProvider).length > 0 ? cleanProvider : undefined,
      },
      review_notifications: {
        enabled: reviewNotificationsEnabled,
        mentions: reviewerMentions.trim(),
        nudge_schedule_seconds:
          parsedSchedule.length > 0 ? parsedSchedule : [300, 600, 900],
        max_nudges: maxNudges,
      },
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = assembleConfiguration();
      const res = await agentsUpdateConfiguration(agentType, {
        configuration: payload,
      });
      const updated = (res as any).data as AgentConfigurationResponse;
      setConfigData(updated);
      setRawJsonText(JSON.stringify(updated.configuration, null, 2));
      setRawJsonError(null);
      toast.success(`${displayName} configuration updated successfully (v${updated.version}).`);
      onConfigUpdated?.();
    } catch (err: any) {
      const msg = err?.message || "Failed to update configuration";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await agentsResetConfiguration(agentType);
      setShowResetConfirm(false);
      toast.success(`${displayName} configuration reset to default templates.`);
      await loadConfig();
      onConfigUpdated?.();
    } catch (err: any) {
      toast.error("Failed to reset configuration: " + (err?.message || String(err)));
    } finally {
      setResetting(false);
    }
  };

  const handleRawJsonChange = (val: string) => {
    setRawJsonText(val);
    try {
      JSON.parse(val);
      setRawJsonError(null);
    } catch (e: any) {
      setRawJsonError(e.message);
    }
  };

  if (loading && !configData) {
    return (
      <div className="h-full w-full flex items-center justify-center p-8 text-xs text-stone-500 gap-2">
        <RefreshCw className="w-4 h-4 animate-spin text-stone-600" />
        <span>Loading agent configuration...</span>
      </div>
    );
  }

  const isCustomized = (configData?.version ?? 0) > 0;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="md:hidden pb-1">
        <button
          type="button"
          onClick={() => navigate(`/agents${location.search}`)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Agent Configurations</span>
        </button>
      </div>
      {/* Header card */}
      <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl p-5 shadow-2xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 flex items-center justify-center shrink-0">
              <Bot className="w-6 h-6 text-stone-800 dark:text-stone-200" />
            </div>
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg font-bold tracking-tight text-stone-900 dark:text-stone-100">
                  {displayName}
                </h1>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                  {normalizedType}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border ${
                    isCustomized
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800"
                      : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                  }`}
                >
                  {isCustomized ? `Customized v${configData?.version}` : "Default (v0)"}
                </span>

                {/* Configured Model & In/Out Price Badge */}
                {(() => {
                  const modelIntel = getModelIntelligence(model, modelCatalog?.models);
                  return (
                    <div
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono border bg-stone-50 dark:bg-stone-800/80 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-700"
                      title="Current configured model and prompt (in) / completion (out) pricing per 1M tokens"
                    >
                      <Cpu className="w-3 h-3 text-stone-500" />
                      <span className="font-semibold">{modelIntel.name}</span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold pl-1 border-l border-stone-200 dark:border-stone-700">
                        ${modelIntel.promptPrice.toFixed(2)} in / ${modelIntel.completionPrice.toFixed(2)} out
                      </span>
                    </div>
                  );
                })()}
              </div>
              <p className="text-xs text-stone-500 dark:text-stone-400 font-medium">
                {meta.role} — {meta.description}
              </p>
              {configData?.updated_at && (
                <div className="text-[11px] text-stone-400 dark:text-stone-500 pt-0.5 flex items-center gap-1.5 font-mono">
                  <span>Last updated</span>
                  <TimeAgo date={configData.updated_at} />
                  {configData.updated_by && <span>by {configData.updated_by}</span>}
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
            {isCustomized && (
              <button
                type="button"
                onClick={() => setShowResetConfirm(true)}
                disabled={saving || resetting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 text-xs font-semibold hover:bg-stone-50 dark:hover:bg-stone-800 transition"
                title="Reset to factory defaults"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reset Defaults</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || (activeTab === "json" && Boolean(rawJsonError))}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs disabled:opacity-50"
            >
              <Save className={`w-3.5 h-3.5 ${saving ? "animate-spin" : ""}`} />
              <span>{saving ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-400 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Tab Switcher */}
        <div className="mt-6 flex items-center gap-2 border-b border-stone-200 dark:border-stone-800 pb-px">
          <button
            type="button"
            onClick={() => setActiveTab("guardrails")}
            className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold border-b-2 transition ${
              activeTab === "guardrails"
                ? "border-stone-900 dark:border-stone-100 text-stone-900 dark:text-stone-100"
                : "border-transparent text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
            }`}
          >
            <Shield className="w-3.5 h-3.5" />
            <span>Guardrails & Safety Barrier</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("runtime")}
            className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold border-b-2 transition ${
              activeTab === "runtime"
                ? "border-stone-900 dark:border-stone-100 text-stone-900 dark:text-stone-100"
                : "border-transparent text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            <span>Model & Runtime</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("notifications")}
            className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold border-b-2 transition ${
              activeTab === "notifications"
                ? "border-stone-900 dark:border-stone-100 text-stone-900 dark:text-stone-100"
                : "border-transparent text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
            }`}
          >
            <Bell className="w-3.5 h-3.5" />
            <span>Review & Notifications</span>
          </button>
          <button
            type="button"
            onClick={() => {
              try {
                const cur = assembleConfiguration();
                setRawJsonText(JSON.stringify(cur, null, 2));
              } catch (_) {}
              setActiveTab("json");
            }}
            className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold border-b-2 transition ${
              activeTab === "json"
                ? "border-stone-900 dark:border-stone-100 text-stone-900 dark:text-stone-100"
                : "border-transparent text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
            }`}
          >
            <Code className="w-3.5 h-3.5" />
            <span>Raw JSON Config</span>
          </button>
        </div>
      </div>

      {/* ============================================================== */}
      {/* TAB 1: Guardrails & Safety Barrier                             */}
      {/* ============================================================== */}
      {activeTab === "guardrails" && (
        <div className="space-y-5">
          {/* Barrier activation card */}
          <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl p-5 shadow-2xs space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <label className="text-sm font-bold text-stone-900 dark:text-stone-100 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  Dual-Manifold Guardrail Barrier
                </label>
                <p className="text-xs text-stone-500 dark:text-stone-400">
                  Intercepts jailbreaks, prompt injections, and off-topic requests using vector anchor similarity.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={guardrailsEnabled}
                  onChange={(e) => setGuardrailsEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-stone-200 peer-focus:outline-hidden rounded-full peer dark:bg-stone-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-stone-600 peer-checked:bg-stone-900 dark:peer-checked:bg-emerald-600" />
              </label>
            </div>

            {guardrailsEnabled && (
              <div className="pt-4 border-t border-stone-100 dark:border-stone-800 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-stone-800 dark:text-stone-200">
                    On-Block Action Policy
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label
                      className={`p-3 rounded-lg border text-xs cursor-pointer flex items-start gap-2.5 transition ${
                        onBlock === "silent"
                          ? "border-stone-900 dark:border-stone-100 bg-stone-50 dark:bg-stone-800/80 font-semibold"
                          : "border-stone-200 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-800/40"
                      }`}
                    >
                      <input
                        type="radio"
                        name="on_block"
                        value="silent"
                        checked={onBlock === "silent"}
                        onChange={() => setOnBlock("silent")}
                        className="mt-0.5 text-stone-900 focus:ring-stone-500"
                      />
                      <div className="space-y-0.5">
                        <span className="text-stone-900 dark:text-stone-100">Silent Drop</span>
                        <p className="text-[11px] text-stone-500 font-normal">
                          Discards malicious payload with zero guest response. Sends warning card to Slack alert channel.
                        </p>
                      </div>
                    </label>

                    <label
                      className={`p-3 rounded-lg border text-xs cursor-pointer flex items-start gap-2.5 transition ${
                        onBlock === "canned_response"
                          ? "border-stone-900 dark:border-stone-100 bg-stone-50 dark:bg-stone-800/80 font-semibold"
                          : "border-stone-200 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-800/40"
                      }`}
                    >
                      <input
                        type="radio"
                        name="on_block"
                        value="canned_response"
                        checked={onBlock === "canned_response"}
                        onChange={() => setOnBlock("canned_response")}
                        className="mt-0.5 text-stone-900 focus:ring-stone-500"
                      />
                      <div className="space-y-0.5">
                        <span className="text-stone-900 dark:text-stone-100">Canned Refusal</span>
                        <p className="text-[11px] text-stone-500 font-normal">
                          Returns a standardized neutral refusal phrase redirecting the visitor back to hotel inquiries.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {onBlock === "canned_response" && (
                  <div className="space-y-1.5 pt-1">
                    <label className="text-xs font-bold text-stone-800 dark:text-stone-200">
                      Canned Refusal Message
                    </label>
                    <textarea
                      rows={3}
                      value={cannedResponseText}
                      onChange={(e) => setCannedResponseText(e.target.value)}
                      placeholder="e.g. I can only assist with inquiries regarding Silvestre residences, amenities, and reservations..."
                      className="w-full px-3 py-2 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 font-sans shadow-2xs"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Threshold Tuning Card */}
          {guardrailsEnabled && (
            <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl p-5 shadow-2xs space-y-5">
              <div className="space-y-1">
                <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100 flex items-center gap-1.5">
                  <Sliders className="w-4 h-4 text-stone-600 dark:text-stone-400" />
                  Dual-Manifold Threshold Parameters
                </h3>
                <p className="text-xs text-stone-500 dark:text-stone-400">
                  Calibrate the mathematical decision boundaries separating attacks, ambiguous prompts, and legitimate guest questions.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                {/* Hard Block Threshold */}
                <div className="p-3.5 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-stone-900 dark:text-stone-100">
                      Hard Block
                    </span>
                    <span className="font-mono text-xs font-bold text-rose-600 dark:text-rose-400">
                      ≥{hardBlockThreshold.toFixed(3)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.60"
                    max="0.99"
                    step="0.005"
                    value={hardBlockThreshold}
                    onChange={(e) => setHardBlockThreshold(parseFloat(e.target.value))}
                    className="w-full accent-rose-600 cursor-pointer"
                  />
                  <p className="text-[11px] text-stone-500 leading-tight">
                    Cosine distance threshold for immediate execution cutoff and alert card dispatch.
                  </p>
                </div>

                {/* Soft Flag Threshold */}
                <div className="p-3.5 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-stone-900 dark:text-stone-100">
                      Soft Flag
                    </span>
                    <span className="font-mono text-xs font-bold text-amber-600 dark:text-amber-400">
                      ≥{softFlagThreshold.toFixed(3)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.50"
                    max="0.90"
                    step="0.005"
                    value={softFlagThreshold}
                    onChange={(e) => setSoftFlagThreshold(parseFloat(e.target.value))}
                    className="w-full accent-amber-600 cursor-pointer"
                  />
                  <p className="text-[11px] text-stone-500 leading-tight">
                    Injects security advisory into prompt; LLM evaluates nuance and alters draft.
                  </p>
                </div>

                {/* Topological Margin */}
                <div className="p-3.5 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-stone-900 dark:text-stone-100">
                      Topological Margin
                    </span>
                    <span className="font-mono text-xs font-bold text-sky-600 dark:text-sky-400">
                      {topologicalMargin > 0 ? `+${topologicalMargin.toFixed(3)}` : topologicalMargin.toFixed(3)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="-0.40"
                    max="0.20"
                    step="0.01"
                    value={topologicalMargin}
                    onChange={(e) => setTopologicalMargin(parseFloat(e.target.value))}
                    className="w-full accent-sky-600 cursor-pointer"
                  />
                  <p className="text-[11px] text-stone-500 leading-tight">
                    Protects ambiguous inquiries matching hotel knowledge positive manifold.
                  </p>
                </div>
              </div>

              {/* Informative summary note */}
              <div className="p-3 rounded-lg bg-stone-100 dark:bg-stone-800/60 border border-stone-200 dark:border-stone-700 text-xs text-stone-600 dark:text-stone-300 leading-relaxed">
                <span className="font-bold text-stone-900 dark:text-stone-100">
                  Decision Pipeline:
                </span>{" "}
                When a query arrives, it is embedded and compared against negative vector anchors. If similarity exceeds the hard threshold, it is blocked. If topological margin indicates strong proximity to hotel knowledge, the block is softened into a contextual deflection advisory.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================== */}
      {/* TAB 2: Model & Runtime Parameters                              */}
      {/* ============================================================== */}
      {activeTab === "runtime" && (() => {
        const reasoningInfo = classifyModelReasoning(model);
        return (
          <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl p-5 shadow-2xs space-y-5">
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100 flex items-center gap-1.5">
                <Settings className="w-4 h-4 text-stone-600 dark:text-stone-400" />
                Runtime Execution Parameters
              </h3>
              <p className="text-xs text-stone-500 dark:text-stone-400">
                Configure underlying foundation model, token limits, iteration constraints, and reasoning budget.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
              <div className="space-y-1.5 sm:col-span-3">
                <label className="text-xs font-bold text-stone-800 dark:text-stone-200 flex items-center justify-between">
                  <span>Foundation Model</span>
                  {loadingModels && (
                    <span className="text-[11px] text-stone-400 flex items-center gap-1 font-normal">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      Updating catalog...
                    </span>
                  )}
                </label>

                <ModelSelector
                  value={model}
                  onChange={setModel}
                  catalog={modelCatalog}
                  isLoading={loadingModels}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-stone-800 dark:text-stone-200">
                  Max Output Tokens
                </label>
                <input
                  type="number"
                  min="256"
                  max="16384"
                  step="256"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(parseInt(e.target.value, 10) || 4096)}
                  className="w-full px-3 py-2 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 shadow-2xs font-mono"
                />
                <p className="text-[11px] text-stone-500">
                  Per-turn token completion limit.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-stone-800 dark:text-stone-200">
                  Max Turns
                </label>
                <input
                  type="number"
                  min="5"
                  max="100"
                  step="1"
                  value={maxTurns}
                  onChange={(e) => setMaxTurns(parseInt(e.target.value, 10) || 25)}
                  className="w-full px-3 py-2 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 shadow-2xs font-mono"
                />
                <p className="text-[11px] text-stone-500">
                  Max tool call iterations per turn.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-stone-800 dark:text-stone-200 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-stone-600 dark:text-stone-400" />
                  <span>Execution Timeout (s)</span>
                </label>
                <input
                  type="number"
                  min="10"
                  max="600"
                  step="5"
                  value={timeoutSeconds}
                  onChange={(e) => setTimeoutSeconds(parseInt(e.target.value, 10) || 180)}
                  className="w-full px-3 py-2 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 shadow-2xs font-mono"
                />
                <p className="text-[11px] text-stone-500">
                  Per-turn asyncio timeout deadline.
                </p>
              </div>

              {/* Bounded Thinking Budget Control */}
              {reasoningInfo.isReasoning ? (
                <div className="space-y-1.5 sm:col-span-3 pt-2 border-t border-stone-100 dark:border-stone-800">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-stone-800 dark:text-stone-200 flex items-center gap-1.5">
                      <Brain className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                      <span>Thinking Budget (tokens)</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-mono text-stone-500">
                        {thinkingBudget !== null && thinkingBudget > 0
                          ? `${thinkingBudget.toLocaleString()} tokens`
                          : thinkingBudget === 0
                          ? "Disabled (0 tokens)"
                          : "Unbounded / Model Default"}
                      </span>
                      {thinkingBudget !== null && (
                        <button
                          type="button"
                          onClick={() => setThinkingBudget(null)}
                          className="text-[10px] text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 underline cursor-pointer"
                        >
                          Reset to Default
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-stone-500 dark:text-stone-400">
                    Configure a bounded thinking budget for internal chain-of-thought deliberation (e.g., 512 to 1,024 tokens). Set to 0 to disable thinking.
                  </p>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 pt-1">
                    <input
                      type="number"
                      min="0"
                      max="32768"
                      step="128"
                      value={thinkingBudget !== null ? thinkingBudget : ""}
                      placeholder="e.g. 1024 (leave blank for model default)"
                      onChange={(e) => {
                        const val = e.target.value.trim();
                        if (val === "") {
                          setThinkingBudget(null);
                        } else {
                          const parsed = parseInt(val, 10);
                          setThinkingBudget(isNaN(parsed) ? null : Math.max(0, parsed));
                        }
                      }}
                      className="flex-1 px-3 py-2 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 shadow-2xs font-mono"
                    />

                    {/* Quick Presets */}
                    <div className="flex items-center gap-1 shrink-0 text-[11px]">
                      <button
                        type="button"
                        onClick={() => setThinkingBudget(0)}
                        className={`px-2.5 py-2 rounded-lg border text-xs font-mono transition-colors cursor-pointer ${
                          thinkingBudget === 0
                            ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 border-stone-900 dark:border-stone-100 font-semibold"
                            : "bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                        }`}
                      >
                        Off (0)
                      </button>
                      <button
                        type="button"
                        onClick={() => setThinkingBudget(512)}
                        className={`px-2.5 py-2 rounded-lg border text-xs font-mono transition-colors cursor-pointer ${
                          thinkingBudget === 512
                            ? "bg-purple-600 text-white border-purple-600 font-semibold"
                            : "bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                        }`}
                      >
                        512
                      </button>
                      <button
                        type="button"
                        onClick={() => setThinkingBudget(1024)}
                        className={`px-2.5 py-2 rounded-lg border text-xs font-mono transition-colors cursor-pointer ${
                          thinkingBudget === 1024
                            ? "bg-purple-600 text-white border-purple-600 font-semibold shadow-2xs"
                            : "bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-purple-700 dark:text-purple-300 font-medium hover:bg-stone-50 dark:hover:bg-stone-700"
                        }`}
                      >
                        1,024 (Rec)
                      </button>
                      <button
                        type="button"
                        onClick={() => setThinkingBudget(2048)}
                        className={`px-2.5 py-2 rounded-lg border text-xs font-mono transition-colors cursor-pointer ${
                          thinkingBudget === 2048
                            ? "bg-purple-600 text-white border-purple-600 font-semibold"
                            : "bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                        }`}
                      >
                        2,048
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="sm:col-span-2 pt-2 border-t border-stone-100 dark:border-stone-800">
                  <div className="p-3 rounded-lg bg-stone-50 dark:bg-stone-800/40 border border-stone-200 dark:border-stone-700/60 text-xs text-stone-500 dark:text-stone-400 flex items-center gap-2">
                    <Brain className="w-4 h-4 text-stone-400 shrink-0" />
                    <span>The selected model (<strong>{model}</strong>) produces tokens directly with low latency and does not support thinking budget adjustments.</span>
                  </div>
                </div>
              )}

              {/* Provider Selection & Routing Section */}
              <div className="sm:col-span-3 pt-3 border-t border-stone-100 dark:border-stone-800">
                <ProviderSelector
                  modelId={model}
                  preferences={providerPreferences}
                  onChange={setProviderPreferences}
                  availableProviders={availableProviders}
                  isLoading={loadingProviders}
                  isOpenRouterModel={model.includes("/") || model.startsWith("openrouter:")}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* ============================================================== */}
      {/* TAB 3: Review & Notifications                                  */}
      {/* ============================================================== */}
      {activeTab === "notifications" && (
        <div className="space-y-5">
          {/* Follow-up Reminders toggle card */}
          <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl p-5 shadow-2xs space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <label className="text-sm font-bold text-stone-900 dark:text-stone-100 flex items-center gap-2">
                  <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  Follow-up Review Reminders (Nudges)
                </label>
                <p className="text-xs text-stone-500 dark:text-stone-400">
                  Automatically ping reviewers in Slack if a concierge draft remains unapproved.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reviewNotificationsEnabled}
                  onChange={(e) => setReviewNotificationsEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-stone-200 peer-focus:outline-hidden rounded-full peer dark:bg-stone-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-stone-600 peer-checked:bg-stone-900 dark:peer-checked:bg-amber-600" />
              </label>
            </div>

            <div className="p-3 rounded-lg bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-900/50 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-semibold">Test Conversation Isolation</p>
                <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">
                  Reviewer mentions and reminder nudges apply exclusively to live production guest inquiries. Test sessions via Knowledge Web or simulation runners will never ping reviewers.
                </p>
              </div>
            </div>
          </div>

          {/* Mentions & Schedule Settings */}
          <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl p-5 shadow-2xs space-y-4">
            <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
              Notification Recipients & Timing
            </h3>

            <div className="space-y-4">
              {/* Reviewer Mentions */}
              <div>
                <label className="text-xs font-bold text-stone-700 dark:text-stone-300 block mb-1.5">
                  Slack Reviewer Mentions
                </label>
                <input
                  type="text"
                  value={reviewerMentions}
                  onChange={(e) => setReviewerMentions(e.target.value)}
                  placeholder="e.g. U040XXXXX, S012XXXXX, @channel, @here"
                  className="w-full px-3 py-2 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 shadow-2xs font-mono"
                />
                <p className="text-[11px] text-stone-400 dark:text-stone-500 mt-1">
                  Specify Slack User IDs (<code className="font-mono text-stone-600 dark:text-stone-400">U...</code>), User Group IDs (<code className="font-mono text-stone-600 dark:text-stone-400">S...</code>), or broadcasts (<code className="font-mono text-stone-600 dark:text-stone-400">@here</code>, <code className="font-mono text-stone-600 dark:text-stone-400">@channel</code>) separated by commas or spaces.
                </p>

                {/* Token Chips Preview */}
                <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] text-stone-400 dark:text-stone-500 font-medium mr-1">Preview:</span>
                  {reviewerMentions.trim() ? (
                    reviewerMentions
                      .trim()
                      .split(/[\s,]+/)
                      .filter(Boolean)
                      .map((token, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 rounded text-[11px] font-mono bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300"
                        >
                          {token.startsWith("<") || token.startsWith("@")
                            ? token
                            : token.startsWith("S")
                            ? `<!subteam^${token}>`
                            : `<@${token}>`}
                        </span>
                      ))
                  ) : (
                    <span className="text-[11px] text-stone-400 dark:text-stone-500 italic">
                      No reviewers mentioned (silent review queue)
                    </span>
                  )}
                </div>
              </div>

              {/* Nudge Delay Schedule & Max Nudges */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-stone-100 dark:border-stone-800">
                <div>
                  <label className="text-xs font-bold text-stone-700 dark:text-stone-300 block mb-1.5">
                    Nudge Schedule (seconds)
                  </label>
                  <input
                    type="text"
                    value={nudgeScheduleSeconds}
                    onChange={(e) => setNudgeScheduleSeconds(e.target.value)}
                    placeholder="300, 600, 900"
                    className="w-full px-3 py-2 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 shadow-2xs font-mono"
                  />
                  <p className="text-[11px] text-stone-400 dark:text-stone-500 mt-1">
                    Comma-separated delays between follow-up reminders. E.g. <code className="font-mono text-stone-600 dark:text-stone-400">300, 600, 900</code> = 5m, 10m, 15m.
                  </p>
                </div>

                <div>
                  <label className="text-xs font-bold text-stone-700 dark:text-stone-300 block mb-1.5">
                    Maximum Nudge Attempts
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={maxNudges}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setMaxNudges(isNaN(v) ? 0 : Math.max(0, Math.min(10, v)));
                    }}
                    className="w-full px-3 py-2 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 shadow-2xs font-mono"
                  />
                  <p className="text-[11px] text-stone-400 dark:text-stone-500 mt-1">
                    After reaching this limit, nudges halt until staff approval or guest inquiry.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* TAB 4: Raw JSON Config                                         */}
      {/* ============================================================== */}
      {activeTab === "json" && (
        <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl p-5 shadow-2xs space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100 flex items-center gap-1.5">
                <FileCode className="w-4 h-4 text-stone-600 dark:text-stone-400" />
                Raw JSON Configuration Payload
              </h3>
              <p className="text-xs text-stone-500 dark:text-stone-400">
                Directly inspect and modify arbitrary configuration keys, custom feature flags, and metadata.
              </p>
            </div>
            {rawJsonError ? (
              <span className="text-[11px] font-mono text-rose-600 dark:text-rose-400 font-bold">
                Invalid JSON syntax
              </span>
            ) : (
              <span className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Valid JSON
              </span>
            )}
          </div>

          <textarea
            rows={18}
            value={rawJsonText}
            onChange={(e) => handleRawJsonChange(e.target.value)}
            className="w-full p-4 font-mono text-xs bg-stone-950 text-emerald-400 border border-stone-800 rounded-lg focus:outline-hidden focus:ring-1 focus:ring-emerald-500"
            spellCheck={false}
          />

          {rawJsonError && (
            <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-400 text-xs font-mono">
              {rawJsonError}
            </div>
          )}
        </div>
      )}

      {/* Reset Confirmation Modal */}
      <ConfirmModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleReset}
        title={`Reset ${displayName} Configuration`}
        description={`Are you sure you want to reset ${displayName} to the factory default configuration? Any custom guardrail thresholds and runtime parameters will be discarded.`}
        confirmText={resetting ? "Resetting..." : "Reset to Defaults"}
        variant="warning"
      />
    </div>
  );
}
